import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaEdit } from "react-icons/fa";
import axios from "axios";
import { Button, Form, Offcanvas } from "react-bootstrap";
import dayjs from "dayjs";
import useAccess from "../../hooks/useAccess";
import DataTable from "../../components/DataTable";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import AdminPageHeader from "../../components/AdminPageHeader";
import "../../styles/WorkHistoryPage.css";
import "../../styles/JobDescriptionPage.css";
import "../../styles/AdministrationPages.css";

function JobDescriptionPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [records, setRecords] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [form, setForm] = useState({ jd_title: "", jd_body: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const { hasPermission } = useAccess();
  const PERMISSIONS = {
    descriptions: {
      create: hasPermission("Recruitment", "Job Descriptions", "edit"),
      edit: hasPermission("Recruitment", "Job Descriptions", "edit"),
      delete: hasPermission("Recruitment", "Job Descriptions", "delete"),
    },
  };
  const requestConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/job-desc`, requestConfig());
      setRecords(response.data || []);
    } catch (error) {
      console.error("Failed to load job descriptions", error);
    }
  }, [BASE_URL]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openDrawer = (record) => {
    setEditRec(record);
    setForm(record
      ? { jd_title: record.jd_title, jd_body: record.jd_body }
      : { jd_title: "", jd_body: "" });
    setShowDrawer(true);
  };
  const handleSave = async (event) => {
    event.preventDefault();
    try {
      if (editRec) {
        await axios.put(`${BASE_URL}/api/job-desc/${editRec.jd_id}`, form, requestConfig());
      } else {
        await axios.post(`${BASE_URL}/api/job-desc`, form, requestConfig());
      }
      setShowDrawer(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save job description", error);
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      await axios.delete(`${BASE_URL}/api/job-desc/${id}`, requestConfig());
      fetchData();
      alert("Entry deleted successfully.");
    } catch (error) {
      console.error("Failed to delete job description", error);
      alert("Failed to delete entry.");
    }
  };

  const filteredRecords = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return records;
    return records.filter((record) =>
      [record.jd_title, record.jd_body, record.created_by_name, record.updated_by_name]
        .filter(Boolean).join(" ").toLowerCase().includes(query),
    );
  }, [records, searchQuery]);

  const columns = [
    { key: "jd_title", label: "Title" },
    { key: "created_by_name", label: "Created By", render: (record) => record.created_by_name || "N/A" },
    { key: "created_at", label: "Created On", render: (record) => dayjs(record.created_at).format("MMM D, YYYY") },
    { key: "updated_by_name", label: "Updated By", render: (record) => record.updated_by_name || "N/A" },
    { key: "updated_at", label: "Updated On", render: (record) => dayjs(record.updated_at).format("MMM D, YYYY") },
    {
      key: "actions", label: "Actions", sortable: false, width: "150px",
      headerClassName: "text-center", cellClassName: "text-center",
      render: (record) => (
        <div className="actions-wrapper">
          <button className="app-table-action-button is-edit" onClick={() => openDrawer(record)} disabled={!PERMISSIONS.descriptions.edit} title="Edit description" aria-label={`Edit ${record.jd_title}`}>
            <FaEdit />
          </button>
          {PERMISSIONS.descriptions.delete && (
            <button className="app-table-action-button is-delete" onClick={() => handleDelete(record.jd_id)} title="Delete description" aria-label={`Delete ${record.jd_title}`}>
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="admin-standard-page job-description-page">
      <AdminPageHeader
        icon="fas fa-file-lines"
        title="Job Description Repository"
        description="Create and maintain reusable job descriptions for recruitment."
        actions={PERMISSIONS.descriptions.create && (
          <Button variant="primary" onClick={() => openDrawer(null)}>
            Add Description <i className="fas fa-plus" aria-hidden="true" />
          </Button>
        )}
      />
      <section className="admin-standard-workspace">
        <div className="admin-standard-workspace-body">
          <ReportFilterPanel>
            <div className="admin-standard-filter-grid admin-standard-filter-grid--single">
              <label className="admin-standard-filter-field">
                <span>Search</span>
                <input type="search" className="form-control" placeholder="Title, content, or creator..." value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
              </label>
            </div>
          </ReportFilterPanel>
          <DataTable columns={columns} data={filteredRecords} rowKey="jd_id" itemsPerPage={10} emptyMessage="No job descriptions found." tableClassName="job-description-table" />
        </div>
      </section>
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="start" className="offcanvas-wide">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{editRec ? "Edit Description" : "Add New Description"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control name="jd_title" value={form.jd_title} onChange={(event) => setForm({ ...form, jd_title: event.target.value })} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description Body</Form.Label>
              <Form.Control as="textarea" rows={6} name="jd_body" value={form.jd_body} onChange={(event) => setForm({ ...form, jd_body: event.target.value })} />
            </Form.Group>
            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={() => setShowDrawer(false)}>Cancel</Button>
              <Button type="submit">Save</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

export default JobDescriptionPage;
